import { AttendanceClockStatus } from '../../api/scheduler/models/attendance-clock-status';
import { Rotation } from '../../api/scheduler/models/rotation';
import { WingType } from '../../api/scheduler/models/wing-type';

export type AttendanceWing = WingType;
export type AttendanceRotation = Rotation;
export type AttendancePunctuality = AttendanceClockStatus;
/** Matches the `sort` field names of the list endpoint. */
export type AttendanceSortKey = 'name' | 'wingType' | 'rotation' | 'delayIn' | 'delayOut';
export type AttendanceSortDir = 'asc' | 'desc';

/** The Base shown on an attendance row, with the colour and display code set in Base Management. */
export interface AttendanceRowBase {
  code: string;
  name: string;
  colorCode: string;
  displayCode: string;
}

/** One crew member's clock-in / clock-out record for a single day. */
export interface AttendanceLogEntry {
  /** Crew id. */
  id: number;
  crewName: string;
  /** Display form of the crew id. */
  employeeCode: string;
  initials: string;
  /** Null when the allocation has no known Base. */
  base: AttendanceRowBase | null;
  wing: AttendanceWing;
  rotation: AttendanceRotation;
  /** Null for Fixed Wing and for Rotor Wing crew with no shift that day. */
  shiftName: string | null;
  /** False when AMC could not supply this crew member's attendance; every time and delay is then null. */
  attendanceAvailable: boolean;
  /** ISO date-time of the first clock-in, or null. */
  clockInAt: string | null;
  /** Null for Fixed Wing, with no clock-in, or when the shift has no start time. */
  clockInStatus: AttendancePunctuality | null;
  /** Seconds past the shift start; only set when late. */
  delayInSeconds: number | null;
  /** ISO date-time of the last clock-out, or null. */
  clockOutAt: string | null;
  clockOutStatus: AttendancePunctuality | null;
  /** Seconds past the shift end; only set when late. */
  delayOutSeconds: number | null;
}

export interface AttendanceLogBase {
  code: string;
  displayCode: string;
  name: string;
}

/** A shift offered by the Assigned Shift filter; one per Base, so the same name can repeat across Bases. */
export interface AttendanceShiftOption {
  /** Sent in the `shift` filter. */
  id: number;
  name: string;
  baseCode: string;
  baseName: string;
  /** False for the default Day and Night shifts; true for a Base-specific shift. */
  custom: boolean;
}

/** Where the shift options stand, so the filter can show a spinner, the list, or a retry. */
export type AttendanceShiftOptionsStatus = 'loading' | 'loaded' | 'failed';

/**
 * Empty list = no restriction on that field. `shifts` holds shift IDs as strings (they travel through chip toggles
 * and the URL as text) plus `UNASSIGNED_SHIFT` for Rotor Wing days with no shift.
 */
export interface AttendanceLogFilters {
  wings: AttendanceWing[];
  baseCodes: string[];
  rotations: AttendanceRotation[];
  shifts: string[];
  clockIn: AttendancePunctuality[];
  clockOut: AttendancePunctuality[];
}

export const NO_ATTENDANCE_FILTERS: AttendanceLogFilters = {
  wings: [],
  baseCodes: [],
  rotations: [],
  shifts: [],
  clockIn: [],
  clockOut: [],
};

export interface AttendanceLogQuery {
  /** Day to show, `yyyy-MM-dd`. */
  date: string;
  search: string;
  filters: AttendanceLogFilters;
  /** `null` keeps the server's default order (earliest clock-in first). */
  sortKey: AttendanceSortKey | null;
  sortDir: AttendanceSortDir;
  /** Zero-based. */
  page: number;
  size: number;
}

export interface AttendanceLogPage {
  content: AttendanceLogEntry[];
  totalElements: number;
  totalPages: number;
}

export const EMPTY_ATTENDANCE_LOG_PAGE: AttendanceLogPage = {
  content: [],
  totalElements: 0,
  totalPages: 0,
};

/** Matches the `sort` field names of the crew log endpoint. */
export type CrewLogSortKey = 'date' | 'wingType' | 'delayIn' | 'delayOut';

/** Filters on the crew log; there is no Base or Rotation filter because the log is for one crew member. */
export type CrewLogFilters = Omit<AttendanceLogFilters, 'baseCodes' | 'rotations'>;

export const NO_CREW_LOG_FILTERS: CrewLogFilters = {
  wings: [],
  shifts: [],
  clockIn: [],
  clockOut: [],
};

export interface CrewLogQuery {
  crewId: number;
  /** Inclusive range, `yyyy-MM-dd`. */
  from: string;
  to: string;
  search: string;
  filters: CrewLogFilters;
  sortKey: CrewLogSortKey;
  sortDir: AttendanceSortDir;
  /** Zero-based. */
  page: number;
  size: number;
}

export interface CrewLogHeader {
  crewId: number;
  /** Display form of the crew id, such as `020`. */
  employeeCode: string;
  name: string;
  wing: AttendanceWing;
  /** The crew member's rotation, read from the log rows; null when none has been loaded. */
  rotation: AttendanceRotation | null;
}

export interface CrewLogSummary {
  shiftsLogged: number;
  lateArrivals: number;
  /** `0h 4m`, or an em dash when there is nothing to average. */
  avgDelayInLabel: string;
  avgWorkLabel: string;
}

/** One allocated day of the crew member. */
export interface CrewLogRow {
  /** `yyyy-MM-dd`. */
  date: string;
  /** Null when the allocation has no known Base. */
  base: AttendanceRowBase | null;
  wing: AttendanceWing;
  rotation: AttendanceRotation;
  shiftName: string | null;
  clockInAt: string | null;
  clockInStatus: AttendancePunctuality | null;
  delayInSeconds: number | null;
  clockOutAt: string | null;
  clockOutStatus: AttendancePunctuality | null;
  delayOutSeconds: number | null;
}

/** What a phone card shows of one day; both a list entry and a crew log row carry it. */
export type AttendanceLogDay = Omit<CrewLogRow, 'date' | 'base'>;

export interface CrewLogResult {
  header: CrewLogHeader;
  summary: CrewLogSummary;
  rows: CrewLogRow[];
  totalElements: number;
  totalPages: number;
}

/** List screen state carried in the URL so Back to Attendance Logs can restore it. */
export interface AttendanceListState {
  date: string;
  search: string;
  filters: AttendanceLogFilters;
  sortKey: AttendanceSortKey | null;
  sortDir: AttendanceSortDir;
  page: number;
  size: number;
}
